import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
  TextField,
  Autocomplete,
  Skeleton,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import SecurityIcon from '@mui/icons-material/Security';
import RouterIcon from '@mui/icons-material/Router';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WifiIcon from '@mui/icons-material/Wifi';
import LaptopIcon from '@mui/icons-material/Laptop';
import SpeakerImage from '../../media/Lamp Poses-07-1.jpg';
import { AuthContext } from '../../AuthContext';
import { useApi } from '../../hooks/useApi';

function Index() {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const jApi = useApi();
  const navigate = useNavigate();

  const [selectedAccount, setSelectedAccount] = useState();
  const [accounts, setAccounts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  
  const fetchData = async () => {
    try {
      setErrorMessage("");
      setIsLoading(true);
      const response = await jApi.request('GET', 'cma/account');

      const options = response.data
        .map(item => `${item.name} (${item.code})`)
        .sort((a, b) => a.localeCompare(b));

      setAccounts(options);

      let selected_account = sessionStorage.getItem('account');
      let selectedItem = options.find(option => option.includes("(" + selected_account + ")"));
      if (selectedItem) {
        setSelectedAccount(selectedItem);
      } else {
        setSelectedAccount(options[0]);
      }
    } catch (ex) {
      setErrorMessage(ex.toString());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function extractCodeFromLabel(label) {
    const match = label.match(/\((.*?)\)/);
    return match ? match[1] : null;
  }

  const handleOnChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedAccount(newValue);
    sessionStorage.setItem('account', extractCodeFromLabel(newValue));
  };

  // Gradient header style
  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };


  return (
    <Box sx={{
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box
            sx={{
              pt: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeTwoToneIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 500,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Operation Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Hero Section with Account Switcher */}
        <Fade in timeout={600}>
          <Box
            sx={{
              ...headerGradient,
              p: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              {/* Title Section */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Operation Center
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Central hub for operational monitoring and management
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Account Selector */}
              <Grid size={{ xs: 12, md: 6 }}>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                ) : accounts && accounts.length > 0 ? (
                  <Autocomplete
                    options={accounts}
                    value={selectedAccount || null}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Account" variant="outlined" />
                    )}
                    onChange={handleOnChange}
                    isOptionEqualToValue={(option, value) => option === value}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(10px)',
                      },
                    }}
                  />
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* SonicWALL Section */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Grow in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      }}
                    >
                      <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        SonicWALL
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Firewall management and security monitoring
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<RouterIcon />}
                    onClick={() => navigate('/operation/sonicwall')}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      },
                    }}
                  >
                    Manage SonicWALL
                  </Button>
                </CardActions>
              </Card>
            </Grow>
          </Grid>

          {/* SonicWALL Audit Section */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Grow in timeout={900}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.error.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.2)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                          }}
                        >
                          <AssessmentIcon sx={{ fontSize: 32, color: 'error.main' }} />
                        </Box>
                    
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.warning.main} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        SonicWALL Audit
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Security best practice compliance review
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/operation/sonicwall/audit')}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.warning.main} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.4)}`,
                        background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.warning.dark} 100%)`,
                      },
                    }}
                  >
                    View Audit Results
                  </Button>
                </CardActions>
              </Card>
            </Grow>
          </Grid>

          {/* Unifi Health Section (Audit + Performance + Client, consolidated) */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Grow in timeout={1000}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                            border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          }}
                        >
                          <WifiIcon sx={{ fontSize: 32, color: 'info.main' }} />
                        </Box>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Unifi Health
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Audit, Performance &amp; Client - one site at a time
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/operation/unifi')}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(theme.palette.info.main, 0.4)}`,
                        background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                      },
                    }}
                  >
                    View Audit Results
                  </Button>
                </CardActions>
              </Card>
            </Grow>
          </Grid>

          {/* Windows Endpoint Audit Section */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Grow in timeout={1100}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                            border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          }}
                        >
                          <LaptopIcon sx={{ fontSize: 32, color: 'info.main' }} />
                        </Box>
                
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Windows Endpoint Audit
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        NOC endpoint health compliance review
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/operation/windows/audit')}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 20px ${alpha(theme.palette.info.main, 0.4)}`,
                        background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                      },
                    }}
                  >
                    Windows Audit
                  </Button>
                </CardActions>
              </Card>
            </Grow>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Index;